import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowRight, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { services } from '../data/mock';

const ServiceCard = ({ service }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500">
      {/* Gallery Carousel */}
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {service.gallery.map((image, index) => (
              <div key={index} className="flex-[0_0_100%] min-w-0">
                <img
                  src={image}
                  alt={`${service.title} ${index + 1}`}
                  className="w-full h-80 object-cover"
                />
              </div>
            ))}
          </div>
        </div>
        
        {/* Navigation Buttons */}
        <button
          onClick={scrollPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
          aria-label="Previous image"
        >
          <ChevronLeft className="text-[#484237]" size={24} />
        </button>
        <button
          onClick={scrollNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
          aria-label="Next image"
        >
          <ChevronRight className="text-[#484237]" size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="p-8">
        <h3 className="text-3xl font-bold text-[#484237] mb-4">
          {service.title}
        </h3>
        <p className="text-[#728481] mb-6 leading-relaxed">
          {service.description}
        </p>

        {/* Features */}
        <ul className="space-y-3 mb-6">
          {service.features.map((feature, index) => (
            <li key={index} className="flex items-center space-x-3">
              <CheckCircle className="text-[#D7AC78] flex-shrink-0" size={20} />
              <span className="text-[#484237]">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <a
          href="/contato"
          className="inline-flex items-center text-[#D7AC78] font-semibold hover:text-[#c49a68] transition-colors group"
        >
          Request a quote
          <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
        </a>
      </div>
    </div>
  );
};

const Servicos = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#F8E6C6] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#D7AC78] font-semibold text-sm uppercase tracking-wider mb-4 block">
              Our Services
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#484237] mb-6 leading-tight">
              Complete Renovation Solutions
            </h1>
            <p className="text-xl text-[#728481] leading-relaxed">
              From planning to execution, we offer specialized services to transform 
              your residential and commercial spaces with quality and high-standard design
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#F8E6C6]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#D7AC78] font-semibold text-sm uppercase tracking-wider mb-4 block">
              Our Process
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#484237] mb-4">
              How We Work
            </h2>
            <p className="text-lg text-[#728481] max-w-2xl mx-auto">
              A structured process to ensure excellence at every stage of your project
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                number: "01",
                title: "Initial Consultation",
                description: "Meeting to understand your needs, expectations, and budget"
              },
              {
                number: "02",
                title: "Planning",
                description: "Project development, detailed timeline, and final budget"
              },
              {
                number: "03",
                title: "Execution",
                description: "Renovation with qualified team, following the highest standards"
              },
              {
                number: "04",
                title: "Delivery",
                description: "Completion with thorough inspection and quality guarantee"
              }
            ].map((step, index) => (
              <div
                key={index}
                className="relative bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="text-6xl font-bold text-[#D7AC78]/10 absolute top-4 right-4">
                  {step.number}
                </div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-[#D7AC78] text-white rounded-full flex items-center justify-center font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-[#484237] mb-3">{step.title}</h3>
                  <p className="text-[#728481]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D7AC78] font-semibold text-sm uppercase tracking-wider mb-4 block">
                Advantages
              </span>
              <h2 className="text-4xl font-bold text-[#484237] mb-6 leading-tight">
                Why Choose Massimo?
              </h2>
              <div className="space-y-4">
                {[
                  "Top-tier materials and premium finishes",
                  "Team of qualified and experienced professionals",
                  "Commitment to deadlines and total transparency",
                  "Quality guarantee on all services",
                  "Personalized service from start to finish",
                  "Custom projects tailored to your needs"
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#D7AC78] flex-shrink-0 mt-1" size={20} />
                    <span className="text-lg text-[#484237]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-[#D7AC78] to-[#c49a68] p-8 rounded-2xl shadow-2xl text-white">
                <h3 className="text-3xl font-bold mb-6">Request a Quote</h3>
                <p className="text-lg mb-6 text-white/90">
                  Contact us and receive a personalized proposal for your project
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} />
                    <span>Free consultation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} />
                    <span>Detailed quote</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} />
                    <span>No obligation</span>
                  </li>
                </ul>
                <a
                  href="/contato"
                  className="inline-flex items-center px-8 py-4 bg-white text-[#D7AC78] font-medium rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2" size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#484237] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Schedule a free consultation and discover how to transform your spaces
          </p>
          <a
            href="/contato"
            className="inline-flex items-center px-8 py-4 bg-[#D7AC78] text-white font-medium rounded-lg hover:bg-[#c49a68] transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
          >
            Speak with a Specialist
            <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Servicos;
